import clsx from 'clsx';
import moment from 'moment-timezone';

import { ExpertRequestStage, ExpertRequestState } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';

import { effectiveStage } from '../../utils/effectiveStage';
import { ER_STAGE_PILL } from '../erStageTokens';

export interface ErStageBannerProps {
  stage?: ExpertRequestStage | null;
  /** Dual-written legacy axis; drives the banner for null-stage rows via effectiveStage. */
  state?: ExpertRequestState | string | null;
  holdReason?: string | null;
  holdResumeDate?: string | null;
  /** When provided, the Draft banner shows the Submit-for-Sourcing CTA. */
  onSubmit?: () => void;
  /** When provided, the On-Hold banner shows the Resume CTA. */
  onResume?: () => void;
  submitting?: boolean;
  resuming?: boolean;
  className?: string;
}

const CONTAINER =
  'flex flex-col gap-12 rounded-lg px-20 py-10 sm:flex-row sm:items-center sm:justify-between';

// The v2 Button hardcodes font-bold with no weight prop, so !font-normal is the only
// call-site override for the requested non-bold, uppercase banner CTAs (ENG-5318 review).
const BANNER_CTA_CLASS = 'shrink-0 !font-normal uppercase';

/**
 * ErStageBanner — non-dismissible status banner shown below the tab bar for the
 * Draft and On-Hold stages (ENG-5290 T2). Presentational: the parent wires the
 * Submit/Resume callbacks (via useErTransitions) and the in-flight flags.
 * Renders nothing for Active or Closed — Closed surfaces no banner; Reopen lives
 * in the header stage dropdown.
 */
export function ErStageBanner({
  stage,
  state,
  holdReason,
  holdResumeDate,
  onSubmit,
  onResume,
  submitting,
  resuming,
  className,
}: ErStageBannerProps): JSX.Element | null {
  const resolved = effectiveStage(stage, state);

  if (resolved === ExpertRequestStage.Draft) {
    return (
      <div
        data-testid="er-stage-banner"
        className={clsx(CONTAINER, ER_STAGE_PILL[ExpertRequestStage.Draft], className)}
      >
        <p className="text-sm">
          This is a Draft expert request. Review the details and Submit for sourcing when ready.
        </p>
        {onSubmit && (
          <Button
            variant="primary"
            size="medium"
            loading={submitting}
            onClick={onSubmit}
            className={BANNER_CTA_CLASS}
          >
            Submit for Sourcing
          </Button>
        )}
      </div>
    );
  }

  if (resolved === ExpertRequestStage.OnHold) {
    const resumeDate = holdResumeDate ? moment(holdResumeDate).format('MMM D, YYYY') : null;
    const headline = resumeDate
      ? `This request is on hold and expected to resume on ${resumeDate}.`
      : 'This request is on hold.';
    // Client free-text renders verbatim: nothing follows it, so we never
    // fabricate trailing punctuation to separate it from later copy.
    const reason = holdReason?.trim();

    return (
      <div
        data-testid="er-stage-banner"
        className={clsx(CONTAINER, ER_STAGE_PILL[ExpertRequestStage.OnHold], className)}
      >
        <div className="text-sm flex flex-col gap-8">
          <p>
            <span className="font-semibold">{headline}</span> While on hold:
          </p>
          <ul className="list-disc pl-20">
            <li>Sourcing of new experts is paused</li>
            <li>Already-invited experts can still apply</li>
            <li>Scheduled consultations still happen</li>
            <li>New consultations can’t be requested</li>
          </ul>
          {reason && <p>Reason: {reason}</p>}
        </div>
        {onResume && (
          <Button
            variant="primary"
            size="medium"
            loading={resuming}
            onClick={onResume}
            className={BANNER_CTA_CLASS}
          >
            Resume sourcing
          </Button>
        )}
      </div>
    );
  }

  return null;
}
